<template>
    <div class="relative">
        <button
            @click.prevent="toggleDropdown"
            @keydown.enter="toggleDropdown"
            @keydown.space.prevent="toggleDropdown"
            :disabled="disabled"
            type="button"
            tabindex="0"
            :class="buttonClass"
        >
            <div>{{ label }}</div>
            <div
                v-if="showCounter"
                class="px-1.5 py-0.5 bg-slate-800 dark:bg-slate-600 rounded-[999px] flex-col justify-center items-center gap-2.5 flex text-white text-xs font-medium leading-none w-4"
                :class="{
                    'xl:invisible max-xl:hidden': !modelValue[valueKey],
                }"
            >
                1
            </div>
            <Icon v-if="!hideArrow" icon="chevron-down" class="w-4 h-4" />
        </button>
        <!-- START DROPDOWN -->
        <Transition>
            <div
                v-if="isOpen"
                class="absolute left-0 top-full mt-1 z-[60] min-w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm"
                v-click-outside="() => (isOpen = false)"
            >
                <ul class="max-h-60 overflow-auto px-1.5 py-2">
                    <li
                        v-for="(option, index) in options"
                        :key="index"
                        :class="{ 'bg-slate-100 dark:bg-slate-700': isSelected(option), optionClass }"
                    >
                        <!--  slot for custom options -->
                        <slot :option="option">
                            <!-- fallback content: -->
                            <div
                                class="flex items-center gap-2 py-2 px-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 rounded"
                                @click="toggleSelect(option)"
                            >
                                <p class="text-sm text-slate-700 dark:text-slate-300">
                                    {{ option[labelKey] }}
                                </p>
                            </div>
                        </slot>
                    </li>
                </ul>
            </div>
        </Transition>
    </div>
</template>

<script>
import { computed, ref } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';

export default {
    name: 'UiSingleSelect',
    components: {
        Icon,
    },
    props: {
        options: {
            type: Array,
            required: true,
        },
        modelValue: {
            type: Object,
            default: null,
        },
        labelKey: {
            type: String,
            default: 'label',
        },
        valueKey: {
            type: String,
            default: 'value',
        },
        label: {
            type: String,
            default: 'Select options...',
        },
        buttonClass: {
            type: String,
            default:
                'font-medium whitespace-nowrap rounded shadow border dark:border-slate-700 inline-flex items-center gap-x-1.5 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 hover:border-slate-800 dark:hover:border-slate-500 focus:!ring-0 focus:!ring-offset-0 focus:!outline-none focus:!border-slate-800 dark:focus:!border-slate-500 focus-visible:!ring-0 focus-visible:!outline-none disabled:opacity-50 disabled:text-slate-800 disabled:bg-white dark:disabled:bg-slate-800 disabled:border-slate-300 dark:disabled:border-slate-600',
        },
        dropdownClass: {
            type: String,
            default:
                'absolute mt-1 border rounded-lg bg-white dark:bg-slate-800 shadow-sm border-slate-200 dark:border-slate-700',
        },
        optionClass: {
            type: String,
            default:
                'p-2 rounded justify-start items-center flex gap-2 text-slate-800 dark:text-slate-100 text-sm font-normal leading-tight whitespace-nowrap hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer',
        },
        labelClass: {
            type: String,
            default: 'hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer',
        },
        showCounter: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        hideArrow: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue', 'changed'],
    setup(props, { emit }) {
        const isOpen = ref(false);

        const toggleDropdown = () => {
            isOpen.value = !isOpen.value;
        };

        const isSelected = (option) => {
            if (props.modelValue) {
                return (
                    option[props.valueKey] === props.modelValue[props.valueKey]
                );
            }
            return false;
        };

        const toggleSelect = (option) => {
            if (isSelected(option)) {
                emit('update:modelValue', {});
            } else {
                emit('update:modelValue', option);
            }
            emit('changed');
            isOpen.value = false;
        };

        return {
            isOpen,
            isSelected,
            toggleDropdown,
            toggleSelect,
        };
    },
};
</script>

<style lang="scss" scoped>
.v-enter-active,
.v-leave-active {
    transition: opacity 0.15s ease;
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
}
</style>
